import { z } from 'zod';

/**
 * Ce que les deux plateformes d'agenda — Doctena et Docalia — partagent **réellement** de leur
 * contrat : la boîte de réception de rattachement, la boîte des erreurs, la santé d'un flux.
 *
 * Ces trois objets ne décrivent aucun protocole : ils décrivent nos propres tables
 * (`appointments`, `sync_jobs`, `integration_health`), que les deux intégrations remplissent de la
 * même façon. Les faire exister en deux exemplaires aurait obligé le front à écrire deux fois la
 * même liste, et nous à les faire diverger un jour.
 *
 * Ce qui, en revanche, reste par fournisseur : ses réglages, ses environnements, ses
 * correspondances (calendriers chez l'un, professionnels chez l'autre) et la liste de ses flux.
 * Voir `dto/doctena.ts` et `dto/docalia.ts`.
 */

/* -------------------------------------------------------------------------- */
/* Boîte de réception : rendez-vous sans patient                               */
/* -------------------------------------------------------------------------- */

/**
 * Un rendez-vous tiré d'une plateforme d'agenda qu'aucune règle de rattachement n'a su relier à un
 * dossier. L'identité affichée vient de `appointments.patientSnapshot`, figée telle que le
 * fournisseur l'a envoyée — jamais reconstruite depuis une note libre, comme le faisait la V1
 * (audit §1.15).
 */
export interface IntegrationUnmatchedAppointmentDto {
  appointmentId: string;
  startsAt: string;
  endsAt: string;
  status: string;
  practitionerMemberId: string;
  /** Identité du réservant chez le fournisseur. Nulle s'il n'a transmis aucun patient. */
  snapshot: { firstName: string; lastName: string; email?: string; phone?: string } | null;
  /** Identifiant distant du rendez-vous, utile au support. Ce n'est pas une donnée patient. */
  externalId: string;
}

export interface IntegrationUnmatchedListDto {
  appointments: IntegrationUnmatchedAppointmentDto[];
}

/**
 * Rattachement manuel. Deux formes exclusives : désigner un dossier existant, ou demander la
 * création du dossier depuis l'instantané. `createPatient` déclenche la détection de doublons du
 * module patients : un 409 `duplicate_candidates` laisse l'écran proposer les dossiers voisins.
 */
export const integrationAssignSchema = z.union([
  z.object({ patientId: z.uuid() }).strict(),
  z
    .object({
      createPatient: z.literal(true),
      /** Passer outre les doublons détectés, après revue humaine. */
      acknowledgeDuplicates: z.boolean().optional(),
    })
    .strict(),
]);
export type IntegrationAssignInput = z.infer<typeof integrationAssignSchema>;

export interface IntegrationAssignResultDto {
  appointmentId: string;
  patientId: string;
  /** Vrai si le dossier vient d'être créé depuis l'instantané. */
  patientCreated: boolean;
}

/* -------------------------------------------------------------------------- */
/* Boîte de réception : erreurs                                                */
/* -------------------------------------------------------------------------- */

/**
 * Un travail de synchronisation en échec, tel que l'écran l'affiche. `reason` est un code
 * technique stable, traduit côté client ; `detail` est un message de bibliothèque tronqué.
 * **Jamais de donnée personnelle** : ni nom, ni email, ni identifiant de dossier lisible.
 */
export interface IntegrationErrorJobDto {
  id: string;
  direction: 'push' | 'pull';
  reason: string;
  detail: string | null;
  retryCount: number;
  createdAt: string;
  finishedAt: string | null;
}

export interface IntegrationErrorListDto {
  jobs: IntegrationErrorJobDto[];
}

/* -------------------------------------------------------------------------- */
/* Santé d'un flux                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Santé d'un flux : curseur, dernière réussite, dernier échec, disjoncteur. Le paramètre de type
 * est la liste fermée des flux du fournisseur — deux chez Docalia, trois chez Doctena —, de sorte
 * que le front garde un ensemble de valeurs exact sans qu'on réécrive la structure.
 */
export interface IntegrationStreamStatusDto<Stream extends string = string> {
  stream: Stream;
  /**
   * Sens variable selon le fournisseur : jeton de reprise du flux incrémental chez Doctena,
   * instant du dernier tirage réussi chez Docalia, qui n'en a pas. Nul = jamais tiré.
   */
  cursor: string | null;
  lastSuccessAt: string | null;
  lastErrorAt: string | null;
  /** Cause technique tronquée. Aucune donnée personnelle n'y entre jamais. */
  lastError: string | null;
  consecutiveFailures: number;
  /** Disjoncteur ouvert : tant que cet instant n'est pas passé, le flux n'est pas relancé. */
  pausedUntil: string | null;
}

/** Paramètre de route commun aux deux boîtes de réception. */
export const integrationAppointmentParamSchema = z.object({ appointmentId: z.uuid() }).strict();
export const integrationJobParamSchema = z.object({ jobId: z.uuid() }).strict();
